import { useCallback, useState } from "react";
import axios, { AxiosResponse } from "axios";
import toast from "react-hot-toast";
import { HOME_TILE_CONFIG_APIS } from "@/api_framework/api_config";
import {
  DynamicRibbonFormat,
  HomeTileType,
  IHomeTileApiRow,
  IHomeTileDynamicStatusBody,
  IHomeTileDynamicStatusResponse,
  IHomeTileListResponse,
  IHomeTileOption,
  IRibbonStyle,
} from "@/api_framework/api_modals/HomeTileConfig";
import { useQuickCommerce } from "@/app/hooks/useQuickCommerce";
import { buildAuthHeaders, readErrorMessage, tileUrl } from "./homeTileApiUtils";

const HOME_TILE_TYPES: HomeTileType[] = ["SERVICE", "QCOMM"];

const TILE_TYPE_ALIASES: Record<string, HomeTileType> = {
  service: "SERVICE",
  collection: "QCOMM",
  qcomm: "QCOMM",
};

const normalizeHomeTileType = (value: string | null | undefined): HomeTileType | null =>
  TILE_TYPE_ALIASES[value?.trim().toLowerCase() ?? ""] ?? null;

const toDisplayName = (value: string): string =>
  value.replace(/(^|\s)(\S)/g, (_match, lead: string, initial: string) => `${lead}${initial.toUpperCase()}`);

const toHomeTileOption = (row: IHomeTileApiRow): IHomeTileOption => ({
  tile_id: row.id,
  tile_name: toDisplayName((row.display_name || row.tile_name || "").trim()),
  tile_type: normalizeHomeTileType(row.tile_type) ?? "QCOMM",
  image_url: row.tile_image ?? undefined,
  visible: Boolean(row.visible),
  web_visible: Boolean(row.web_visible),
  is_dynamic_enabled: Boolean(row.is_dynamic_enabled),
  dynamic_ribbon_member_format: row.dynamic_ribbon_member_format ?? null,
  dynamic_ribbon: row.dynamic_ribbon ?? null,
  active_rule_count: row.active_config_count ?? 0,
  updated_at: row.updated_at ?? null,
});

const sortByTileType = (tiles: IHomeTileOption[]): IHomeTileOption[] =>
  [...tiles].sort((a, b) => HOME_TILE_TYPES.indexOf(a.tile_type) - HOME_TILE_TYPES.indexOf(b.tile_type));

export const useHomeTileListAPI = () => {
  const quickCommerce = useQuickCommerce();
  const [tileList, setTileList] = useState<IHomeTileOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdatingDynamicStatus, setIsUpdatingDynamicStatus] = useState<boolean>(false);
  const accessTokenKey = quickCommerce.localStorage.ACCESS_TOKEN.key;

  const execute = useCallback(async () => {
    setIsLoading(true);
    try {
      const res: AxiosResponse<IHomeTileListResponse> = await axios.get(HOME_TILE_CONFIG_APIS.TILES, {
        headers: buildAuthHeaders(accessTokenKey),
      });
      if (res.data?.status) {
        setTileList(sortByTileType((res.data.data?.tiles ?? []).map(toHomeTileOption)));
      } else {
        setTileList([]);
        toast.error(res.data?.message || "Failed to load home tiles.");
      }
    } catch (error) {
      setTileList([]);
      toast.error(readErrorMessage(error) || "Failed to load home tiles. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [accessTokenKey]);

  const updateDynamicStatus = useCallback(
    async (
      tileId: string,
      isDynamicEnabled: boolean,
      ribbonStyle?: IRibbonStyle,
      ribbonFormat?: DynamicRibbonFormat,
    ): Promise<boolean> => {
      setIsUpdatingDynamicStatus(true);
      try {
        const body: IHomeTileDynamicStatusBody = {
          is_dynamic_enabled: isDynamicEnabled,
          ...(ribbonFormat ? { dynamic_ribbon_member_format: ribbonFormat } : {}),
          ...(ribbonStyle ? { dynamic_ribbon: ribbonStyle } : {}),
        };
        const res: AxiosResponse<IHomeTileDynamicStatusResponse> = await axios.put(
          tileUrl(HOME_TILE_CONFIG_APIS.TILES, tileId),
          body,
          { headers: buildAuthHeaders(accessTokenKey) },
        );
        if (!res.data?.status) {
          toast.error(res.data?.message || "Failed to update the dynamic status.");
          return false;
        }
        setTileList((current) =>
          current.map((tile) =>
            tile.tile_id === tileId
              ? {
                  ...tile,
                  is_dynamic_enabled: isDynamicEnabled,
                  dynamic_ribbon_member_format: (isDynamicEnabled && ribbonFormat) || tile.dynamic_ribbon_member_format,
                  dynamic_ribbon: ribbonStyle ?? tile.dynamic_ribbon,
                }
              : tile,
          ),
        );
        toast.success(isDynamicEnabled ? "Dynamic status turned on." : "Dynamic status turned off.");
        return true;
      } catch (error) {
        toast.error(readErrorMessage(error) || "Failed to update the dynamic status. Please try again.");
        return false;
      } finally {
        setIsUpdatingDynamicStatus(false);
      }
    },
    [accessTokenKey],
  );

  return { tileList, isLoading, isUpdatingDynamicStatus, execute, updateDynamicStatus };
};
